import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useApp } from "../../contexts/AppContext";
import { supabase } from "../../lib/supabase";

interface ConvRow {
  id: string;
  consumer_id: string;
  provider_id: string;
  purchased_item_title: string | null;
  last_message: string | null;
  last_message_at: string | null;
  consumer_unread: number;
  provider_unread: number;
  other_profile: {
    id: string;
    name: string;
    service_name: string | null;
    avatar_url: string | null;
  } | null;
}

interface MsgRow {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
}

export default function MessagesPage() {
  const { currentUser, session } = useApp();
  const [conversations, setConversations] = useState<ConvRow[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MsgRow[]>([]);
  const [inputText, setInputText] = useState("");
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isProvider = currentUser?.role === "provider";

  const loadConversations = useCallback(async () => {
    if (!currentUser?.id) return;
    const field = isProvider ? "provider_id" : "consumer_id";
    const { data } = await supabase
      .from("conversations")
      .select("*")
      .eq(field, currentUser.id)
      .order("last_message_at", { ascending: false });

    if (!data) { setLoadingConvs(false); return; }

    const otherIds = data.map((c) => (isProvider ? c.consumer_id : c.provider_id));
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, name, service_name, avatar_url")
      .in("id", otherIds.filter(Boolean));

    const profileMap = Object.fromEntries((profiles || []).map((p) => [p.id, p]));

    setConversations(data.map((c) => ({
      ...c,
      other_profile: profileMap[isProvider ? c.consumer_id : c.provider_id] ?? null,
    })));
    setLoadingConvs(false);
  }, [currentUser?.id, isProvider]);

  const loadMessages = useCallback(async (convId: string) => {
    setLoadingMsgs(true);
    const { data } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", convId)
      .order("created_at", { ascending: true });
    setMessages(data || []);
    setLoadingMsgs(false);

    // Mark as read
    if (currentUser?.id) {
      const unreadField = isProvider ? "provider_unread" : "consumer_unread";
      await supabase
        .from("conversations")
        .update({ [unreadField]: 0 })
        .eq("id", convId);
      await supabase
        .from("messages")
        .update({ is_read: true })
        .eq("conversation_id", convId)
        .neq("sender_id", currentUser.id);
    }
  }, [currentUser?.id, isProvider]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (activeId) loadMessages(activeId);
  }, [activeId, loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!inputText.trim() || !activeId || !currentUser?.id || sending) return;
    setSending(true);
    const content = inputText.trim();
    setInputText("");

    const { data: newMsg } = await supabase
      .from("messages")
      .insert({ conversation_id: activeId, sender_id: currentUser.id, content, is_read: false })
      .select()
      .maybeSingle();

    if (newMsg) setMessages((prev) => [...prev, newMsg]);

    const unreadField = isProvider ? "consumer_unread" : "provider_unread";
    await supabase
      .from("conversations")
      .update({ last_message: content, last_message_at: new Date().toISOString(), [unreadField]: 1 })
      .eq("id", activeId);

    await loadConversations();
    setSending(false);
  };

  const activeConv = conversations.find((c) => c.id === activeId);
  const totalUnread = conversations.reduce((a, c) => a + (isProvider ? c.provider_unread : c.consumer_unread), 0);

  const getAvatar = (conv: ConvRow) =>
    conv.other_profile?.avatar_url ||
    `https://public.readdy.ai/ai/img_res/8fcac49f26d7a51e5aca59bac00b0f2f.jpg${(conv.other_profile?.id || "x").slice(0, 8)}&orientation=squarish`;

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 86400000) return d.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days}日前`;
    }
    return d.toLocaleDateString("ja-JP", { month: "short", day: "numeric" });
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-ivory">
        <Navbar />
        <main className="pt-32 text-center px-4">
          <i className="ri-lock-line text-5xl text-charcoal/20 block mb-4" />
          <p className="text-charcoal/50 font-sans mb-4">メッセージ機能はログイン後にご利用いただけます</p>
          <Link to="/login" className="bg-deep-green text-white px-6 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
            ログイン
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="pt-20 pb-0">
        <div className="max-w-screen-xl mx-auto px-4 py-6">
          <div className="mb-4">
            <p className="font-display italic text-gold text-xs tracking-widest mb-1">Messages</p>
            <h1 className="font-serif text-deep-green text-2xl font-light">
              メッセージ
              {totalUnread > 0 && (
                <span className="ml-2 text-sm bg-gold text-white px-2 py-0.5 rounded-full font-sans">{totalUnread}</span>
              )}
            </h1>
            <p className="text-charcoal/50 text-xs font-sans mt-1">
              {isProvider ? "購入者からのメッセージ" : "購入済みの事業者にのみ送信できます"}
            </p>
          </div>

          <div
            className="bg-white rounded-3xl border border-stone-100 overflow-hidden flex"
            style={{ height: "calc(100vh - 220px)", minHeight: "500px" }}
          >
            {/* Sidebar */}
            <div className="w-72 border-r border-stone-100 flex flex-col flex-shrink-0 hidden md:flex">
              <div className="p-4 border-b border-stone-50">
                <p className="text-xs font-sans text-charcoal/40 font-medium">会話一覧</p>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loadingConvs ? (
                  <div className="p-4 space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="flex gap-3 animate-pulse">
                        <div className="w-10 h-10 rounded-full bg-stone-200 flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-stone-200 rounded w-3/4" />
                          <div className="h-2 bg-stone-200 rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full px-4 text-center">
                    <i className="ri-message-3-line text-3xl text-charcoal/20 mb-2" />
                    <p className="text-xs text-charcoal/40 font-sans">まだ会話がありません</p>
                    {!isProvider && (
                      <Link to="/providers" className="mt-3 text-xs text-teal font-sans hover:underline cursor-pointer">
                        事業者を探す →
                      </Link>
                    )}
                  </div>
                ) : (
                  conversations.map((c) => {
                    const unread = isProvider ? c.provider_unread : c.consumer_unread;
                    const otherName = isProvider
                      ? (c.other_profile?.name || "購入者")
                      : (c.other_profile?.service_name || c.other_profile?.name || "事業者");
                    return (
                      <button
                        key={c.id}
                        onClick={() => setActiveId(c.id)}
                        className={`w-full px-4 py-4 flex items-start gap-3 cursor-pointer transition-colors text-left border-b border-stone-50 ${activeId === c.id ? "bg-ivory" : "hover:bg-stone-50"}`}
                      >
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-stone-100">
                          <img src={getAvatar(c)} alt={otherName} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1 mb-0.5">
                            <p className="text-sm font-sans text-charcoal font-medium truncate">{otherName}</p>
                            <p className="text-xs text-charcoal/40 font-sans whitespace-nowrap">
                              {c.last_message_at ? formatTime(c.last_message_at) : ""}
                            </p>
                          </div>
                          <p className="text-xs text-charcoal/50 font-sans truncate">{c.last_message || "…"}</p>
                          {c.purchased_item_title && (
                            <p className="text-xs text-teal/60 font-sans mt-0.5 truncate">{c.purchased_item_title}</p>
                          )}
                        </div>
                        {unread > 0 && (
                          <span className="w-5 h-5 flex items-center justify-center bg-gold text-white text-xs rounded-full font-sans flex-shrink-0">
                            {unread}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Chat area */}
            {activeConv ? (
              <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <div className="px-6 py-4 border-b border-stone-100 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-stone-100">
                    <img src={getAvatar(activeConv)} alt="" className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-charcoal text-sm font-medium">
                      {isProvider
                        ? (activeConv.other_profile?.name || "購入者")
                        : (activeConv.other_profile?.service_name || activeConv.other_profile?.name || "事業者")}
                    </p>
                    {activeConv.purchased_item_title && (
                      <p className="text-xs text-charcoal/40 font-sans">
                        <i className="ri-shopping-bag-line mr-1" />{activeConv.purchased_item_title}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-shield-check-line text-charcoal/30 text-xs" />
                    <p className="text-xs text-charcoal/30 font-sans hidden sm:block">監視対象</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-4">
                  {loadingMsgs ? (
                    <div className="flex items-center justify-center flex-1">
                      <i className="ri-loader-4-line animate-spin text-2xl text-teal" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center flex-1 text-center">
                      <div>
                        <i className="ri-chat-1-line text-4xl text-charcoal/20 block mb-2" />
                        <p className="text-charcoal/40 font-sans text-sm">会話を始めましょう</p>
                      </div>
                    </div>
                  ) : (
                    messages.map((m) => {
                      const isMe = m.sender_id === currentUser?.id;
                      return (
                        <div key={m.id} className={`flex gap-3 ${isMe ? "flex-row-reverse" : "flex-row"}`}>
                          {!isMe && (
                            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-stone-100">
                              <img src={getAvatar(activeConv)} alt="" className="w-full h-full object-cover object-top" />
                            </div>
                          )}
                          <div className={`max-w-[70%] ${isMe ? "items-end" : "items-start"} flex flex-col gap-1`}>
                            <div className={`px-4 py-3 rounded-2xl text-sm font-sans leading-relaxed ${isMe ? "bg-deep-green text-white rounded-tr-sm" : "bg-stone-100 text-charcoal rounded-tl-sm"}`}>
                              {m.content}
                            </div>
                            <p className="text-xs text-charcoal/30 font-sans">{formatTime(m.created_at)}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="px-4 py-4 border-t border-stone-100">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 relative">
                      <textarea
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        placeholder="メッセージを入力…"
                        rows={2}
                        className="w-full px-4 py-3 rounded-2xl border border-stone-200 text-sm font-sans text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-teal transition-colors resize-none bg-ivory"
                      />
                    </div>
                    <button
                      onClick={handleSend}
                      disabled={!inputText.trim() || sending}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-deep-green text-white cursor-pointer hover:bg-teal transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {sending ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-send-plane-fill text-sm" />}
                    </button>
                  </div>
                  <p className="text-xs text-charcoal/30 font-sans mt-1.5 px-1">
                    <i className="ri-shield-check-line mr-0.5" />メッセージは安全管理のため監視対象です。
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center px-4">
                <div>
                  <i className="ri-message-3-line text-5xl text-charcoal/20 block mb-3" />
                  <p className="text-charcoal/40 font-sans text-sm">
                    {conversations.length === 0 ? "まだメッセージがありません" : "会話を選択してください"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
